import { useCallback, useMemo, useState } from 'react';
import useLatestCallback from 'use-latest-callback';
import { DialogActions, Divider, Step, StepLabel, Stepper } from '@mui/material';
import { Box } from '@mui/system';
import { toast } from 'react-toastify';
import { SubmitHandler, useFormContext, useFormState } from 'react-hook-form';

import { AddAthleteFormValues } from './AddWrestlerUtils';
import { log } from '@utils/logging';
import { AddWrestlerStepsEnum } from './AddWrestlerUtils';
import LoadingButton from '@atoms/LoadingButton';
import { StepIconComponent, StepperConnector } from '@components/molecules/Stepper';
import { updateUserAthlete } from '@modules/supabase/users';
import { stringFromError } from '@utils/utils';
import useUser from '@hooks/useUser';
import { addChildToUser, inviteUserToTeam } from '@modules/api';
import { InviteUserToTeamBody, STATUS, TEAM_ROLE } from '@type/db-types';
import useTeams from '@hooks/useTeams';

const steps = ['Name', 'Birthday', 'Stats', 'Invite'];

interface Props {
    step: number;
    onClose: () => void;
    onPrevClick: () => void;
    onNextClick: () => void;
    addingToTeam: boolean;
}

export default function NextBackButtonGroup({
    step,
    onClose,
    onNextClick,
    onPrevClick,
    addingToTeam,
}: Props) {
    const { user, refresh: refreshUser } = useUser();
    const [disabled, setDisabled] = useState(false);
    const enableNext = useEnableNext(step);
    const { isLoading, isSubmitting } = useFormState();
    const { selectedTeam, refreshMembers } = useTeams();

    const { handleSubmit, watch } = useFormContext<AddAthleteFormValues>();
    const email = watch('email');

    const onLastStep = step === AddWrestlerStepsEnum.WRESTLER_INVITE;

    const onSubmit: SubmitHandler<AddAthleteFormValues> = useLatestCallback(async (values) => {
        console.log(`AddWrestlerModal onSubmit`, { values });

        if (user?.id) {
            const isExistingUser = values.id;
            setDisabled(true);

            if (isExistingUser) {
                await updateUserAthlete(values, 'addWrestler submit')
                    .then(() => toast.success('Wrestler updated'))
                    .catch((e) => log(`[AddWrestlerForm:update] ${stringFromError(e)}`))
                    .finally(() => setDisabled(false));
            } else {
                if (addingToTeam) {
                    const invite: InviteUserToTeamBody = {
                        ...values,
                        teamId: selectedTeam?.id as string,
                        role: TEAM_ROLE.ATHLETE,
                        teamStatus: STATUS.ACTIVE, // TODO: should this ever be INVITED? eg. if they haven't paid their membership yet...
                    };
                    await inviteUserToTeam(invite);
                    refreshMembers();
                } else {
                    // Create new athlete and user as a child of the current user
                    await addChildToUser(values);
                    refreshUser();
                }
                setDisabled(false);
            }
            onClose();
        } else {
            log(`AddWrestlerModal onSubmit: user not found`);
            toast.error(`Error saving wrestler, refresh and try again`);
        }
    });

    // Some props for NextButton change depending on which step you're on
    const nextButtonProps: {
        onClick?: () => void;
        children: string;
    } = useMemo(() => {
        if (onLastStep) {
            return {
                children: email?.length ? 'Create Wrestler' : 'Skip & Create Wrestler',
                onClick: () =>
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    handleSubmit(onSubmit, (_error, _event) => {
                        toast.error(`Saving wrestler failed, please try again`);
                    })(),
            };
        } else {
            return {
                children: 'Next',
                onClick: onNextClick,
            };
        }
    }, [email?.length, handleSubmit, onLastStep, onNextClick, onSubmit]);

    return (
        <Box paddingTop={2}>
            <Divider />
            <Box sx={{ paddingTop: 2, paddingX: 1 }}>
                <Stepper
                    alternativeLabel
                    activeStep={step}
                    connector={<StepperConnector />}
                    sx={{ paddingBottom: 1 }}
                >
                    {steps.length > 1
                        ? steps.map((label) => (
                              <Step key={label}>
                                  <StepLabel StepIconComponent={StepIconComponent}>
                                      {label}
                                  </StepLabel>
                              </Step>
                          ))
                        : null}
                </Stepper>
                <DialogActions>
                    <LoadingButton onClick={onClose}>Cancel</LoadingButton>
                    <Box sx={{ flex: '1 1 auto' }} />
                    {step > 0 ? (
                        <LoadingButton type="button" onClick={onPrevClick}>
                            Prev
                        </LoadingButton>
                    ) : null}

                    <LoadingButton
                        disabled={!enableNext || disabled || isLoading || isSubmitting}
                        loading={disabled || isLoading || isSubmitting}
                        variant="contained"
                        {...nextButtonProps}
                    />
                </DialogActions>
                {/* {onLastStep && Object.keys(errors).length ? (
                    <>
                        {Object.keys(errors).map((errorKey) => (
                            <ErrorText
                                key={errorKey}
                                message={`${errorKey}: ${errors?.[errorKey]?.message}`}
                            />
                        ))}
                    </>
                ) : null} */}
            </Box>
        </Box>
    );
}

const userInfoFields: (keyof AddAthleteFormValues)[] = ['firstName', 'lastName'];
const birthFields: (keyof AddAthleteFormValues)[] = ['birthday', 'gender'];
const statsFields: (keyof AddAthleteFormValues)[] = ['weight'];
const inviteFields: (keyof AddAthleteFormValues)[] = ['email'];

function useEnableNext(step: number) {
    const { errors, dirtyFields } = useFormState<AddAthleteFormValues>();

    const noErrorsForStep = useCallback(
        (fields: (keyof AddAthleteFormValues)[]) =>
            !fields.some((field) => Object.keys(errors).includes(field)),
        [errors],
    );

    const everyFieldTouched = useCallback(
        (fields: (keyof AddAthleteFormValues)[]) => fields.every((f) => dirtyFields[f]),
        [dirtyFields],
    );

    // console.log(`errors`, { errors, userInfoFields: noErrorsForStep(userInfoFields) });

    if (step === AddWrestlerStepsEnum.WRESTLER_NAME)
        return everyFieldTouched(userInfoFields) && noErrorsForStep(userInfoFields);
    // TODO: Add more validation
    else if (step === AddWrestlerStepsEnum.WRESTLER_BIRTH)
        return everyFieldTouched(birthFields) && noErrorsForStep(birthFields);
    else if (step === AddWrestlerStepsEnum.WRESTLER_STATS)
        return everyFieldTouched(statsFields) && noErrorsForStep(statsFields);
    else if (step === AddWrestlerStepsEnum.WRESTLER_INVITE) return noErrorsForStep(inviteFields);
    else {
        log(`useEnableNext unrecognized step: ${step}`);
        return true;
    }
}
